import { BOARD, BOARD_SIZE, MAX_LEVEL, QUARANTINE_TILE, groupTiles } from './board'
import { DEAL_BY_ID, DEAL_CARDS } from './cards.deals'
import { EVENT_BY_ID, EVENT_CARDS } from './cards.events'
import { CHARACTERS, PROFESSIONS } from './characters'
import {
  assetSaleNet,
  diceCap,
  hasEffect,
  hasSomethingToSell,
  isFinanciallyFree,
  liquidationValue,
  mortgageAmount,
  mult,
  paydayDetail,
  propertySaleNet,
  totalAssets,
  unmortgageCost,
} from './finance'
import { randInt, shuffle } from './rng'
import type {
  ActiveEffect,
  Asset,
  DealCard,
  EventCard,
  EventEffectSpec,
  GameSettings,
  GameState,
  LogEntry,
  Player,
  Prompt,
  Tile,
} from './types'

export const QUARANTINE_TURNS = 2
export const QUARANTINE_BAIL = 40_000
export const START_BONUS = 50_000
export const DOWN_RATE = 0.2
export const DOWN_RATE_LEVERAGED = 0.1
/**
 * ค่าผ่อนต่อเดือน = 1.5% ของยอดกู้ ตั้งใจให้เลเวอเรจ "คุ้มกว่าเงินสดต่อบาทที่ลง"
 * ประมาณสองเท่า แลกกับรายจ่ายคงที่ที่ดันเป้าหมายหลุดวงจรหนูให้ไกลขึ้น
 * และเจ็บหนักเวลาเจอเหตุการณ์ที่ทุบค่าเช่า
 */
export const LOAN_MONTHLY_RATE = 0.015
export { PROPERTY_SELL_RATE, SELL_HAIRCUT } from './finance'

export interface PlayerConfig {
  name: string
  isBot: boolean
  characterId: string
  professionId: string
}

export const DEFAULT_SETTINGS: GameSettings = {
  freedomMultiplier: 1,
  globalEvents: true,
  personalDebuffs: true,
  botSpeed: 900,
}

/* ------------------------------------------------------------------ */
/* ยูทิลิตี้                                                          */
/* ------------------------------------------------------------------ */

export const cur = (g: GameState): Player => g.players[g.current]

function uid(g: GameState, prefix: string): string {
  g.uidSeq += 1
  return `${prefix}${g.uidSeq}`
}

export function pushLog(
  g: GameState,
  playerId: number | null,
  emoji: string,
  text: string,
  tone: LogEntry['tone'] = 'neutral',
): void {
  g.logSeq += 1
  g.log.unshift({ id: g.logSeq, round: g.round, playerId, emoji, text, tone })
  if (g.log.length > 120) g.log.length = 120
}

function push(g: GameState, p: Prompt): void {
  if (g.prompt) g.queue.push(p)
  else g.prompt = p
}

export function closePrompt(g: GameState): void {
  g.prompt = g.queue.shift() ?? null
}

export const alivePlayers = (g: GameState): Player[] => g.players.filter((p) => !p.bankrupt)

const others = (g: GameState, id: number): Player[] =>
  g.players.filter((p) => p.id !== id && !p.bankrupt)

export function diceTotal(g: GameState): number {
  if (!g.dice) return 0
  return g.dice.reduce<number>((s, d) => s + (d ?? 0), 0)
}

/* ------------------------------------------------------------------ */
/* สร้างเกม                                                           */
/* ------------------------------------------------------------------ */

export function createGame(cfg: {
  players: PlayerConfig[]
  settings?: Partial<GameSettings>
  seed?: number
}): GameState {
  const settings = { ...DEFAULT_SETTINGS, ...cfg.settings }
  const seedHolder = { seed: (cfg.seed ?? 20260730) >>> 0 }

  const players: Player[] = cfg.players.map((pc, i) => {
    const character = CHARACTERS.find((c) => c.id === pc.characterId) ?? CHARACTERS[i % CHARACTERS.length]
    const profession =
      PROFESSIONS.find((p) => p.id === pc.professionId) ?? PROFESSIONS[i % PROFESSIONS.length]
    return {
      id: i,
      name: pc.name.trim() || character.name,
      isBot: pc.isBot,
      character,
      profession,
      cash: profession.startingCash,
      pos: 0,
      children: 0,
      assets: [],
      liabilities: [],
      effects: [],
      skipTurns: 0,
      quarantine: 0,
      shield: profession.perkId === 'sturdy' ? 1 : 0,
      bankrupt: false,
      freedomRound: null,
      doubles: 0,
      rerollLeft: profession.perkId === 'lucky' ? 1 : 0,
      stats: { paydays: 0, rentPaid: 0, rentEarned: 0, dealsBought: 0 },
    }
  })


  const g: GameState = {
    phase: 'idle',
    round: 1,
    players,
    current: 0,
    tiles: BOARD.map(() => ({ ownerId: null, level: 0, mortgaged: false })),
    dice: null,
    rolling: false,
    prompt: null,
    queue: [],
    globalEffects: [],
    jackpot: 100_000,
    dealDeck: shuffle(seedHolder, DEAL_CARDS).map((c) => c.id),
    eventDeck: shuffle(seedHolder, buildEventPool(settings)).map((c) => c.id),
    log: [],
    logSeq: 0,
    uidSeq: 0,
    winner: null,
    rollAgain: false,
    seed: seedHolder.seed,
    settings,
  }

  pushLog(g, null, '🎬', 'เกมเริ่ม! เป้าหมาย: ทำ Passive Income ให้มากกว่ารายจ่ายรวม เพื่อหลุดพ้นวงจรหนู', 'big')
  pushLog(g, null, '🛰️', 'ระวังเหตุการณ์ไซไฟ — พายุสุริยะ, AI แย่งงาน และการแช่แข็งไครโอ', 'neutral')
  return g
}

/* ------------------------------------------------------------------ */
/* เอฟเฟกต์                                                           */
/* ------------------------------------------------------------------ */

function addEffect(
  g: GameState,
  target: Player | 'global',
  spec: Omit<ActiveEffect, 'uid'>,
): void {
  // +1 เพราะจะถูกหักหนึ่งครั้งตอนจบตา/จบรอบที่ได้รับ ทำให้ระยะเวลาตรงกับที่การ์ดบอก
  const eff: ActiveEffect = { ...spec, uid: uid(g, 'ef'), turnsLeft: spec.turnsLeft + 1 }
  if (target === 'global') g.globalEffects.push(eff)
  else target.effects.push(eff)
}

const tick = (list: ActiveEffect[]): ActiveEffect[] =>
  list.map((e) => ({ ...e, turnsLeft: e.turnsLeft - 1 })).filter((e) => e.turnsLeft > 0)

/* ------------------------------------------------------------------ */
/* เงิน                                                               */
/* ------------------------------------------------------------------ */

/** รับเงิน — พารามิเตอร์ `_g` คงไว้ให้เข้าคู่กับ debit() ที่ต้องใช้ state */
export function credit(_g: GameState, p: Player, amount: number): void {
  p.cash += amount
}

/** จ่ายเงิน — ถ้าเงินสดติดลบจะบังคับให้ระดมทุน หรือประกาศล้มละลาย */
export function debit(g: GameState, p: Player, amount: number): void {
  p.cash -= amount
  if (p.cash < 0) checkSolvency(g, p)
}

/**
 * เงินสดติดลบ:
 *  • ถ้าเป็นผู้เล่นคน "ที่กำลังถึงตา" → เปิดหน้าต่างให้เลือกขายเอง
 *  • ถ้าเป็นบอท หรือคนที่โดนหางเลขจากการ์ด (เช่น "จัดปาร์ตี้ลอยฟ้า" ที่หักเงินคนอื่น)
 *    → ระบบขายให้อัตโนมัติ ไม่งั้นเขาจะติดลบค้างจนเกมเดินต่อไม่ได้
 */
function checkSolvency(g: GameState, p: Player): void {
  if (p.cash >= 0 || p.bankrupt) return
  if (liquidationValue(g, p) < 0) {
    declareBankrupt(g, p)
    return
  }
  if (p.id === g.current && !p.isBot) {
    const dup = (q: Prompt) => q.t === 'bankruptWarning' && q.playerId === p.id
    if (!(g.prompt && dup(g.prompt)) && !g.queue.some(dup)) {
      push(g, { t: 'bankruptWarning', playerId: p.id, needed: -p.cash })
    }
    return
  }
  autoLiquidate(g, p)
}

/** ขายสินทรัพย์ → จำนอง → ขายโฉนด จนกว่าเงินสดจะไม่ติดลบ */
function autoLiquidate(g: GameState, p: Player): void {
  let guard = 0
  while (p.cash < 0 && guard++ < 80) {
    const worstAsset = p.assets
      .slice()
      .sort((a, b) => a.cashflow / Math.max(1, a.value) - b.cashflow / Math.max(1, b.value))[0]
    if (worstAsset) {
      sellAssetFor(g, p, worstAsset.uid)
      continue
    }
    const toMortgage = g.tiles
      .map((ts, id) => ({ ts, id }))
      .filter((x) => x.ts.ownerId === p.id && !x.ts.mortgaged)
      .sort((a, b) => a.ts.level - b.ts.level || (BOARD[a.id].price ?? 0) - (BOARD[b.id].price ?? 0))[0]
    if (toMortgage) {
      mortgageFor(g, p, toMortgage.id)
      continue
    }
    const toSell = g.tiles
      .map((ts, id) => ({ ts, id }))
      .filter((x) => x.ts.ownerId === p.id)
      .sort((a, b) => (BOARD[a.id].price ?? 0) - (BOARD[b.id].price ?? 0))[0]
    if (toSell) {
      sellPropertyFor(g, p, toSell.id)
      continue
    }
    break
  }
  if (p.cash < 0) declareBankrupt(g, p)
}

export function declareBankrupt(g: GameState, p: Player): void {
  p.bankrupt = true
  p.cash = 0
  p.assets = []
  p.liabilities = []
  p.effects = []
  g.tiles.forEach((ts) => {
    if (ts.ownerId === p.id) {
      ts.ownerId = null
      ts.level = 0
      ts.mortgaged = false
    }
  })
  pushLog(g, p.id, '💀', `${p.name} ล้มละลาย! ทรัพย์สินทั้งหมดกลับคืนสู่ธนาคาร`, 'bad')
  push(g, {
    t: 'info',
    title: `${p.name} ล้มละลาย`,
    emoji: '💀',
    text: 'หนี้ท่วมจนขายทุกอย่างก็ไม่พอ — ออกจากเกม บทเรียน: อย่าให้รายจ่ายวิ่งแซงกระแสเงินสด',
  })
  checkGameOver(g)
}

function checkGameOver(g: GameState): void {
  const alive = alivePlayers(g)
  if (alive.length <= 1) {
    g.winner = alive[0]?.id ?? null
    g.phase = 'gameover'
    if (alive[0]) pushLog(g, alive[0].id, '👑', `${alive[0].name} เป็นผู้รอดคนสุดท้าย — ชนะ!`, 'big')
  }
}

function checkFreedom(g: GameState, p: Player): void {
  if (p.bankrupt || g.phase === 'gameover') return
  if (!isFinanciallyFree(g, p)) return
  p.freedomRound = g.round
  g.winner = p.id
  g.phase = 'gameover'
  pushLog(g, p.id, '🏆', `${p.name} หลุดพ้นวงจรหนูแล้ว! Passive Income แซงรายจ่ายรวม`, 'big')
  push(g, { t: 'freedom', playerId: p.id })
}

/* ------------------------------------------------------------------ */
/* ค่าเช่า                                                            */
/* ------------------------------------------------------------------ */

export function rentFor(g: GameState, tileId: number, dice: number): number {
  const ts = g.tiles[tileId]
  const tile = BOARD[tileId]
  if (ts.ownerId === null || ts.mortgaged || !tile.rent) return 0
  const owner = g.players[ts.ownerId]
  let base = 0

  if (tile.kind === 'property') {
    const group = groupTiles(tile.group!)
    const ownsAll = group.every((t) => g.tiles[t.id].ownerId === owner.id)
    base = tile.rent[Math.min(ts.level, tile.rent.length - 1)]
    if (ownsAll && ts.level === 0) base *= 2
  } else if (tile.kind === 'transport') {
    const owned = BOARD.filter((t) => t.kind === 'transport' && g.tiles[t.id].ownerId === owner.id)
      .length
    base = tile.rent[Math.max(0, owned - 1)]
  } else if (tile.kind === 'utility') {
    if (hasEffect(g, owner, 'utilityOff')) return 0
    const owned = BOARD.filter((t) => t.kind === 'utility' && g.tiles[t.id].ownerId === owner.id)
      .length
    base = (dice || 7) * tile.rent[Math.max(0, owned - 1)]
  }

  let amount = base * mult(g, owner, 'rentEarnMult')
  if (owner.profession.perkId === 'rentPlus') amount *= 1.15
  return Math.round(amount / 100) * 100
}

/* ------------------------------------------------------------------ */
/* การเดิน                                                            */
/* ------------------------------------------------------------------ */

export function canRoll(g: GameState): boolean {
  const p = cur(g)
  return g.phase === 'idle' && !g.prompt && !p.bankrupt && p.quarantine === 0
}

export function rollDice(g: GameState): void {
  if (!canRoll(g)) return
  const p = cur(g)
  const cap = diceCap(g, p)
  const extra = hasEffect(g, p, 'extraDie')
  const d1 = randInt(g, 1, cap)
  const d2 = randInt(g, 1, cap)
  g.dice = extra ? [d1, d2, randInt(g, 1, cap)] : [d1, d2]
  g.rolling = true
  g.phase = 'moving'
}

export function reroll(g: GameState): void {
  const p = cur(g)
  if (g.phase !== 'moving' || !g.rolling || p.rerollLeft <= 0) return
  p.rerollLeft -= 1
  const cap = diceCap(g, p)
  const extra = hasEffect(g, p, 'extraDie')
  const d1 = randInt(g, 1, cap)
  const d2 = randInt(g, 1, cap)
  g.dice = extra ? [d1, d2, randInt(g, 1, cap)] : [d1, d2]
  pushLog(g, p.id, '🎲', `${p.name} ใช้สิทธิ์ทอยใหม่ (โชคของศิลปิน)`, 'neutral')
}

/** เรียกหลังอนิเมชันลูกเต๋าจบ */
export function commitMove(g: GameState): void {
  if (g.phase !== 'moving') return
  g.rolling = false
  const p = cur(g)
  const total = diceTotal(g)
  const d = g.dice!
  const isDouble = d.length === 2 && d[0] === d[1]
  g.rollAgain = false

  if (isDouble) {
    p.doubles += 1
    if (p.doubles >= 3) {
      pushLog(g, p.id, '🚨', `${p.name} ทอยแต้มคู่ 3 ครั้งติด — ตำรวจไซเบอร์สงสัยว่าโกงระบบสุ่ม!`, 'bad')
      sendToQuarantine(g, p)
      g.phase = 'action'
      return
    }
    g.rollAgain = true
  }

  const reversed = hasEffect(g, p, 'reverse')
  step(g, p, reversed ? -total : total)
  pushLog(
    g,
    p.id,
    '🎲',
    `${p.name} ทอยได้ ${d.join('+')} = ${total}${reversed ? ' (เดินถอยหลัง!)' : ''} → ${BOARD[p.pos].name}`,
  )
  landOn(g, p, total)
  g.phase = 'action'
}

function step(g: GameState, p: Player, delta: number): void {
  const before = p.pos
  let next = (before + delta) % BOARD_SIZE
  if (next < 0) next += BOARD_SIZE
  p.pos = next
  if (delta > 0 && before + delta >= BOARD_SIZE) doPayday(g, p)
}

/** ย้ายตำแหน่งโดยไม่ผ่าน START (สำหรับการ์ดเทเลพอร์ต / หลุมดำ) */
function teleport(g: GameState, p: Player, to: number, collectPayday = false): void {
  const before = p.pos
  p.pos = ((to % BOARD_SIZE) + BOARD_SIZE) % BOARD_SIZE
  if (collectPayday && p.pos < before) doPayday(g, p)
}

function sendToQuarantine(g: GameState, p: Player): void {
  p.pos = QUARANTINE_TILE
  p.quarantine = QUARANTINE_TURNS
  p.doubles = 0
  g.rollAgain = false
  pushLog(g, p.id, '🧊', `${p.name} ถูกส่งเข้าสถานีกักกัน ${QUARANTINE_TURNS} ตา`, 'bad')
  push(g, {
    t: 'info',
    title: 'เข้าสถานีกักกัน',
    emoji: '🧊',
    text: `ต้องรอ ${QUARANTINE_TURNS} ตา หรือจ่ายค่าประกันตัว ฿${QUARANTINE_BAIL.toLocaleString('th-TH')} เพื่อออกทันที`,
  })
}

/* ------------------------------------------------------------------ */
/* Payday                                                             */
/* ------------------------------------------------------------------ */

function doPayday(g: GameState, p: Player): void {
  const detail = paydayDetail(g, p)
  p.stats.paydays += 1
  if (detail.net >= 0) credit(g, p, detail.net)
  else debit(g, p, -detail.net)
  pushLog(
    g,
    p.id,
    detail.net >= 0 ? '💵' : '🩸',
    `${p.name} รับ Payday: เงินเดือน ฿${detail.salary.toLocaleString('th-TH')} + Passive ฿${detail.passive.toLocaleString('th-TH')} − รายจ่าย ฿${(detail.expenses + detail.loans).toLocaleString('th-TH')} = ${detail.net >= 0 ? '+' : '−'}฿${Math.abs(detail.net).toLocaleString('th-TH')}`,
    detail.net >= 0 ? 'good' : 'bad',
  )
  push(g, { t: 'payday', amount: detail.net, detail })
}

/* ------------------------------------------------------------------ */
/* ลงช่อง                                                             */
/* ------------------------------------------------------------------ */

function landOn(g: GameState, p: Player, dice: number): void {
  const tile = BOARD[p.pos]
  switch (tile.kind) {
    case 'start': {
      credit(g, p, START_BONUS)
      pushLog(g, p.id, '🚀', `${p.name} ลงตรงจุดเริ่มต้นเป๊ะ! รับโบนัส ฿${START_BONUS.toLocaleString('th-TH')}`, 'good')
      push(g, {
        t: 'info',
        title: 'ลงจุดเริ่มต้นเป๊ะ!',
        emoji: '🚀',
        text: `โบนัสพิเศษ ฿${START_BONUS.toLocaleString('th-TH')}`,
      })
      break
    }
    case 'property':
    case 'transport':
    case 'utility': {
      const ts = g.tiles[p.pos]
      if (ts.ownerId === null) {
        if (hasEffect(g, p, 'noBuy')) {
          push(g, {
            t: 'info',
            title: 'ซื้อไม่ได้',
            emoji: '🔓',
            text: `บัญชีของ ${p.name} ยังถูกแฮกอยู่ ซื้อ ${tile.name} ไม่ได้`,
          })
        } else {
          push(g, { t: 'buyProperty', tileId: p.pos })
        }
      } else if (ts.ownerId === p.id) {
        // เดิมขึ้นแค่ข้อความบอกให้ไปกดที่แผงพอร์ตโฟลิโอ ซึ่งหายากมากหลังเปลี่ยนเป็น
        // โหมดเต็มจอ (แผงถูกซ่อนหลังปุ่มลอย) ตอนนี้เปิดให้อัปเกรดจากตรงนี้ได้เลย
        push(g, { t: 'ownProperty', tileId: p.pos })
      } else if (ts.mortgaged) {
        push(g, {
          t: 'info',
          title: tile.name,
          emoji: '🏚️',
          text: 'ช่องนี้ถูกจำนองไว้ ไม่ต้องจ่ายค่าเช่า',
        })
      } else {
        const amount = rentFor(g, p.pos, dice)
        push(g, { t: 'payRent', tileId: p.pos, amount, toPlayer: ts.ownerId })
      }
      break
    }
    case 'deal': {
      const card = drawDeal(g)
      if (!card) break
      if (p.profession.perkId === 'dealSense') {
        const second = drawDeal(g)
        push(g, { t: 'deal', card, choices: second ? [card, second] : [card] })
      } else {
        push(g, { t: 'deal', card })
      }
      break
    }
    case 'event': {
      const card = drawEvent(g)
      if (card) push(g, { t: 'event', card })
      break
    }
    case 'tax': {
      const base = totalAssets(g, p)
      let amount = Math.max(20_000, Math.round((base * (tile.taxRate ?? 0.1)) / 1000) * 1000)
      if (p.profession.perkId === 'taxShield') amount = Math.round(amount / 2)
      push(g, { t: 'tax', tileId: p.pos, amount })
      break
    }
    case 'quarantine': {
      push(g, {
        t: 'info',
        title: 'สถานีกักกัน',
        emoji: '🧊',
        text: 'แวะเยี่ยมเพื่อน ๆ ที่ถูกแช่แข็ง — ยังไม่ต้องเข้าไปนอนในแคปซูล',
      })
      break
    }
    case 'gotoQuarantine': {
      sendToQuarantine(g, p)
      break
    }
    case 'jackpot': {
      const amount = g.jackpot
      push(g, { t: 'jackpot', amount })
      break
    }
  }
}

/* ------------------------------------------------------------------ */
/* สำรับการ์ด                                                         */
/* ------------------------------------------------------------------ */

function drawDeal(g: GameState): DealCard | null {
  if (g.dealDeck.length === 0) g.dealDeck = shuffle(g, DEAL_CARDS).map((c) => c.id)
  const id = g.dealDeck.shift()
  return id ? DEAL_BY_ID.get(id) ?? null : null
}

/**
 * สำรับเหตุการณ์ตามสวิตช์ในหน้าตั้งค่า
 * ปิด globalEvents = ตัดการ์ดระดับโลกทิ้งทั้งหมด
 * ปิด personalDebuffs = ตัดเฉพาะโทษรายตัว (บัฟ/การ์ดพิลึกยังอยู่ สำรับจึงไม่มีวันว่าง)
 */
function buildEventPool(settings: GameSettings): EventCard[] {
  return EVENT_CARDS.filter((c) => {
    if (c.scope === 'global') return settings.globalEvents
    return settings.personalDebuffs || c.tone !== 'bad'
  })
}

function drawEvent(g: GameState): EventCard | null {
  if (g.eventDeck.length === 0) {
    g.eventDeck = shuffle(g, buildEventPool(g.settings)).map((c) => c.id)
  }
  const id = g.eventDeck.shift()
  return id ? EVENT_BY_ID.get(id) ?? null : null
}

/* ------------------------------------------------------------------ */
/* การซื้อโฉนด                                                        */
/* ------------------------------------------------------------------ */

export function downPayment(p: Player, price: number): number {
  const rate = p.profession.perkId === 'bigDown' ? DOWN_RATE_LEVERAGED : DOWN_RATE
  return Math.round((price * rate) / 1000) * 1000
}

export function loanMonthly(loan: number): number {
  return Math.round((loan * LOAN_MONTHLY_RATE) / 100) * 100
}

export function canAfford(p: Player, cost: number): boolean {
  return p.cash >= cost
}

export function buyProperty(g: GameState, mode: 'cash' | 'loan'): void {
  if (g.prompt?.t !== 'buyProperty') return
  const tileId = g.prompt.tileId
  const tile = BOARD[tileId]
  const p = cur(g)
  const price = tile.price ?? 0
  const down = mode === 'cash' ? price : downPayment(p, price)
  if (!canAfford(p, down)) return

  debit(g, p, down)
  g.tiles[tileId].ownerId = p.id

  if (mode === 'loan') {
    const loan = price - down
    const monthly = loanMonthly(loan)
    p.liabilities.push({
      uid: uid(g, 'lb'),
      name: `สินเชื่อ ${tile.name}`,
      emoji: '🏦',
      balance: loan,
      monthly,
      tileId,
    })
    pushLog(
      g,
      p.id,
      '🏦',
      `${p.name} ผ่อนซื้อ ${tile.name} — ดาวน์ ฿${down.toLocaleString('th-TH')} กู้ ฿${loan.toLocaleString('th-TH')} ผ่อนเดือนละ ฿${monthly.toLocaleString('th-TH')}`,
      'neutral',
    )
  } else {
    pushLog(g, p.id, '🏠', `${p.name} ซื้อ ${tile.name} ด้วยเงินสด ฿${price.toLocaleString('th-TH')}`, 'good')
  }
  closePrompt(g)
}

export function skipBuy(g: GameState): void {
  if (g.prompt?.t !== 'buyProperty') return
  const tile = BOARD[g.prompt.tileId]
  pushLog(g, cur(g).id, '🙅', `${cur(g).name} ไม่ซื้อ ${tile.name}`)
  closePrompt(g)
}

export function upgradeCostFor(p: Player, tile: Tile): number {
  const base = tile.upgradeCost ?? 0
  return p.profession.perkId === 'cheapLoan' ? Math.round((base * 0.75) / 1000) * 1000 : base
}

export function canUpgrade(g: GameState, p: Player, tileId: number): boolean {
  const tile = BOARD[tileId]
  const ts = g.tiles[tileId]
  if (tile.kind !== 'property' || ts.ownerId !== p.id || ts.mortgaged) return false
  if (ts.level >= MAX_LEVEL) return false
  const group = groupTiles(tile.group!)
  const ownsAll = group.every((t) => g.tiles[t.id].ownerId === p.id && !g.tiles[t.id].mortgaged)
  if (!ownsAll) return false
  // ต้องอัปเกรดกระจายกันในกลุ่มสี (ห่างกันไม่เกิน 1 ระดับ)
  const minLevel = Math.min(...group.map((t) => g.tiles[t.id].level))
  if (ts.level > minLevel) return false
  return canAfford(p, upgradeCostFor(p, tile))
}

export function upgradeProperty(g: GameState, tileId: number): void {
  const p = cur(g)
  if (!canUpgrade(g, p, tileId)) return
  const tile = BOARD[tileId]
  const cost = upgradeCostFor(p, tile)
  debit(g, p, cost)
  g.tiles[tileId].level += 1
  pushLog(
    g,
    p.id,
    '🏗️',
    `${p.name} อัปเกรด ${tile.name} เป็นระดับ ${g.tiles[tileId].level} (จ่าย ฿${cost.toLocaleString('th-TH')})`,
    'good',
  )
}

function mortgageFor(g: GameState, p: Player, tileId: number): void {
  const ts = g.tiles[tileId]
  const tile = BOARD[tileId]
  if (ts.ownerId !== p.id || ts.mortgaged) return
  const amount = mortgageAmount(tileId)
  ts.mortgaged = true
  p.cash += amount
  pushLog(g, p.id, '🏚️', `${p.name} จำนอง ${tile.name} รับ ฿${amount.toLocaleString('th-TH')}`, 'neutral')
}

export function mortgageProperty(g: GameState, tileId: number): void {
  mortgageFor(g, cur(g), tileId)
}

export function unmortgageProperty(g: GameState, tileId: number): void {
  const p = cur(g)
  const ts = g.tiles[tileId]
  const tile = BOARD[tileId]
  if (ts.ownerId !== p.id || !ts.mortgaged) return
  const cost = unmortgageCost(tileId)
  if (!canAfford(p, cost)) return
  debit(g, p, cost)
  ts.mortgaged = false
  pushLog(g, p.id, '🔓', `${p.name} ไถ่ถอน ${tile.name} (จ่าย ฿${cost.toLocaleString('th-TH')})`)
}

function sellPropertyFor(g: GameState, p: Player, tileId: number): void {
  const ts = g.tiles[tileId]
  const tile = BOARD[tileId]
  if (ts.ownerId !== p.id) return
  const net = propertySaleNet(g, p, tileId)
  const loanIdx = p.liabilities.findIndex((l) => l.tileId === tileId)
  if (loanIdx >= 0) p.liabilities.splice(loanIdx, 1)
  ts.ownerId = null
  ts.level = 0
  ts.mortgaged = false
  p.cash += net
  pushLog(
    g,
    p.id,
    '💱',
    `${p.name} ขาย ${tile.name} ได้สุทธิ ${net >= 0 ? '+' : '−'}฿${Math.abs(net).toLocaleString('th-TH')}`,
    net >= 0 ? 'neutral' : 'bad',
  )
}

export function sellProperty(g: GameState, tileId: number): void {
  const p = cur(g)
  sellPropertyFor(g, p, tileId)
  checkSolvency(g, p)
}

/* ------------------------------------------------------------------ */
/* การ์ด DEAL                                                         */
/* ------------------------------------------------------------------ */

export function dealDown(card: DealCard): number {
  return card.down ?? card.price
}

export function acceptDeal(g: GameState, cardId?: string): void {
  if (g.prompt?.t !== 'deal') return
  const chosen =
    (cardId ? g.prompt.choices?.find((c) => c.id === cardId) : undefined) ?? g.prompt.card
  const p = cur(g)
  if (hasEffect(g, p, 'noBuy')) return
  const down = dealDown(chosen)
  if (!canAfford(p, down)) return

  debit(g, p, down)
  const asset: Asset = {
    uid: uid(g, 'as'),
    kind: chosen.kind,
    name: chosen.name,
    emoji: chosen.emoji,
    cost: down,
    cashflow: chosen.cashflow,
    value: chosen.value,
    units: chosen.units,
  }
  p.assets.push(asset)
  p.stats.dealsBought += 1

  const loan = chosen.price - down
  if (loan > 0) {
    p.liabilities.push({
      uid: uid(g, 'lb'),
      name: `สินเชื่อ ${chosen.name}`,
      emoji: '🏦',
      // กระแสเงินสดของการ์ดคิดสุทธิหลังหักค่าผ่อนแล้ว จึงไม่บวกค่าผ่อนซ้ำ
      balance: loan,
      monthly: 0,
    })
  }
  pushLog(
    g,
    p.id,
    chosen.emoji,
    `${p.name} ปิดดีล ${chosen.name} — จ่าย ฿${down.toLocaleString('th-TH')} ได้กระแสเงินสด +฿${chosen.cashflow.toLocaleString('th-TH')}/เดือน`,
    'good',
  )
  closePrompt(g)
  checkFreedom(g, p)
}

export function declineDeal(g: GameState): void {
  if (g.prompt?.t !== 'deal') return
  pushLog(g, cur(g).id, '🙅', `${cur(g).name} ปล่อยดีล ${g.prompt.card.name} ไป`)
  closePrompt(g)
}

function sellAssetFor(g: GameState, p: Player, assetUid: string): void {
  const idx = p.assets.findIndex((a) => a.uid === assetUid)
  if (idx < 0) return
  const asset = p.assets[idx]
  const net = assetSaleNet(p, assetUid)
  const lienIdx = p.liabilities.findIndex((l) => l.name === `สินเชื่อ ${asset.name}`)
  if (lienIdx >= 0) p.liabilities.splice(lienIdx, 1)
  p.assets.splice(idx, 1)
  p.cash += net
  pushLog(
    g,
    p.id,
    '💱',
    `${p.name} ขาย ${asset.name} ได้สุทธิ ${net >= 0 ? '+' : '−'}฿${Math.abs(net).toLocaleString('th-TH')}`,
    net >= 0 ? 'good' : 'bad',
  )
}

export function sellAsset(g: GameState, assetUid: string): void {
  const p = cur(g)
  sellAssetFor(g, p, assetUid)
  checkSolvency(g, p)
}

/* ------------------------------------------------------------------ */
/* การ์ดเหตุการณ์                                                     */
/* ------------------------------------------------------------------ */

function applySpec(g: GameState, p: Player, spec: EventEffectSpec, cardEmoji: string): void {
  switch (spec.t) {
    case 'cash': {
      if (spec.amount >= 0) credit(g, p, spec.amount)
      else debit(g, p, -spec.amount)
      break
    }
    case 'cashPct': {
      const base =
        spec.base === 'assets' ? totalAssets(g, p) : spec.base === 'cash' ? p.cash : p.profession.salary
      const amount = Math.round((base * Math.abs(spec.pct)) / 100) * 100
      if (spec.pct >= 0) credit(g, p, amount)
      else debit(g, p, amount)
      break
    }
    case 'effect': {
      if (spec.effect.kind === 'marketCrash') {
        p.assets.forEach((a) => {
          if (a.kind === 'crypto') a.value = Math.round(a.value * spec.effect.magnitude)
        })
        break
      }
      if (spec.effect.kind === 'marketBoom') {
        p.assets.forEach((a) => {
          a.value = Math.round(a.value * spec.effect.magnitude)
        })
        break
      }
      addEffect(g, spec.scope === 'global' ? 'global' : p, spec.effect)
      break
    }
    case 'skip': {
      p.skipTurns += spec.turns
      g.rollAgain = false
      break
    }
    case 'quarantine': {
      sendToQuarantine(g, p)
      break
    }
    case 'move': {
      if (spec.to === 'random') teleport(g, p, randInt(g, 0, BOARD_SIZE - 1))
      else if (spec.to === 'back') teleport(g, p, p.pos - (spec.steps ?? 3))
      else teleport(g, p, spec.to, true)
      pushLog(g, p.id, cardEmoji, `${p.name} ถูกย้ายไป ${BOARD[p.pos].name}`, 'neutral')
      break
    }
    case 'child': {
      p.children += 1
      pushLog(
        g,
        p.id,
        '👶',
        `${p.name} มีลูกคนที่ ${p.children} — รายจ่ายเพิ่ม ฿${p.profession.childCost.toLocaleString('th-TH')}/เดือน`,
        'bad',
      )
      break
    }
    case 'shield': {
      p.shield += spec.amount
      break
    }
    case 'jackpot': {
      credit(g, p, g.jackpot)
      pushLog(g, p.id, '🎰', `${p.name} เก็บกองกลาง ฿${g.jackpot.toLocaleString('th-TH')}`, 'good')
      g.jackpot = 0
      break
    }
    case 'eachPays': {
      others(g, p.id).forEach((o) => {
        debit(g, o, spec.amount)
        credit(g, p, spec.amount)
      })
      break
    }
    case 'payEach': {
      others(g, p.id).forEach((o) => {
        credit(g, o, spec.amount)
        debit(g, p, spec.amount)
      })
      break
    }
  }
}

export function applyEventCard(g: GameState): void {
  if (g.prompt?.t !== 'event') return
  const card = g.prompt.card
  const p = cur(g)

  if (card.scope === 'personal') {
    if (card.tone === 'bad' && p.shield > 0) {
      p.shield -= 1
      pushLog(g, p.id, '🛡️', `โล่พลังงานของ ${p.name} ดูดซับ "${card.title}" ไว้ได้!`, 'good')
      closePrompt(g)
      return
    }
    card.effects.forEach((s) => applySpec(g, p, s, card.emoji))
    pushLog(
      g,
      p.id,
      card.emoji,
      `${p.name} เจอ "${card.title}"`,
      card.tone === 'good' ? 'good' : card.tone === 'bad' ? 'bad' : 'neutral',
    )
  } else {
    pushLog(g, null, card.emoji, `เหตุการณ์ระดับโลก: ${card.title}`, 'big')
    card.effects.forEach((spec) => {
      if (spec.t === 'effect' && spec.scope === 'global' && spec.effect.kind !== 'marketCrash' && spec.effect.kind !== 'marketBoom') {
        addEffect(g, 'global', spec.effect)
        return
      }
      alivePlayers(g).forEach((target) => applySpec(g, target, spec, card.emoji))
    })
  }
  closePrompt(g)
  alivePlayers(g).forEach((pl) => checkFreedom(g, pl))
}

/* ------------------------------------------------------------------ */
/* ยืนยัน prompt ทั่วไป                                               */
/* ------------------------------------------------------------------ */

export function confirmPrompt(g: GameState): void {
  const prompt = g.prompt
  if (!prompt) return
  switch (prompt.t) {
    case 'payRent': {
      const p = cur(g)
      const owner = g.players[prompt.toPlayer]
      const amount = Math.round(amountAfterPayerEffects(g, p, prompt.amount))
      debit(g, p, amount)
      credit(g, owner, amount)
      p.stats.rentPaid += amount
      owner.stats.rentEarned += amount
      pushLog(
        g,
        p.id,
        '💸',
        `${p.name} จ่ายค่าเช่า ${BOARD[prompt.tileId].name} ฿${amount.toLocaleString('th-TH')} ให้ ${owner.name}`,
        'bad',
      )
      closePrompt(g)
      checkFreedom(g, owner)
      break
    }
    case 'tax': {
      const p = cur(g)
      debit(g, p, prompt.amount)
      g.jackpot += prompt.amount
      pushLog(
        g,
        p.id,
        '🧾',
        `${p.name} จ่าย${BOARD[prompt.tileId].name} ฿${prompt.amount.toLocaleString('th-TH')} (เข้ากองกลาง)`,
        'bad',
      )
      closePrompt(g)
      break
    }
    case 'jackpot': {
      const p = cur(g)
      credit(g, p, prompt.amount)
      g.jackpot = 0
      pushLog(
        g,
        p.id,
        '🏝️',
        prompt.amount > 0
          ? `${p.name} เก็บกองกลางไป ฿${prompt.amount.toLocaleString('th-TH')}`
          : `${p.name} มาถึงท่าอวกาศพักผ่อน แต่กองกลางว่างเปล่า`,
        prompt.amount > 0 ? 'good' : 'neutral',
      )
      closePrompt(g)
      break
    }
    case 'event': {
      applyEventCard(g)
      break
    }
    case 'bankruptWarning': {
      const p = cur(g)
      if (p.cash >= 0) closePrompt(g)
      break
    }
    default:
      closePrompt(g)
  }
}

function amountAfterPayerEffects(g: GameState, payer: Player, amount: number): number {
  return amount * mult(g, payer, 'rentPayMult')
}

/* ------------------------------------------------------------------ */
/* สถานีกักกัน                                                        */
/* ------------------------------------------------------------------ */

/**
 * ยอมแพ้ระหว่างติดลบ — ทางออกสุดท้ายที่การันตีว่าเกมไม่มีวันตัน
 * ใช้เมื่อผู้เล่นขายจนไม่เหลืออะไรแล้วเงินยังไม่พอ หรืออยากจบเลยไม่อยากไล่ขาย
 */
export function surrender(g: GameState): void {
  const p = cur(g)
  if (p.bankrupt || p.cash >= 0) return
  declareBankrupt(g, p)
  if (g.prompt?.t === 'bankruptWarning') closePrompt(g)
  if (g.phase !== 'gameover') {
    g.prompt = null
    g.queue = []
    finishTurn(g)
  }
}

/** ผู้เล่นคนนี้ยังมีทางกู้สถานการณ์อยู่ไหม (ใช้ตัดสินว่าจะโชว์ปุ่มยอมแพ้) */
export function canStillRecover(g: GameState, p: Player): boolean {
  return hasSomethingToSell(g, p) && liquidationValue(g, p) >= 0
}

export function payBail(g: GameState): void {
  const p = cur(g)
  if (p.quarantine <= 0 || !canAfford(p, QUARANTINE_BAIL)) return
  debit(g, p, QUARANTINE_BAIL)
  p.quarantine = 0
  pushLog(g, p.id, '🔓', `${p.name} จ่ายค่าประกันตัว ฿${QUARANTINE_BAIL.toLocaleString('th-TH')} ออกจากสถานีกักกัน`, 'neutral')
}

/* ------------------------------------------------------------------ */
/* จบตา                                                               */
/* ------------------------------------------------------------------ */

export function canEndTurn(g: GameState): boolean {
  if (g.phase === 'gameover') return false
  if (g.prompt) return false
  const p = cur(g)
  if (p.cash < 0) return false
  return g.phase === 'action' || (g.phase === 'idle' && p.quarantine > 0)
}

/**
 * ตานี้ยังมีอะไรให้ตัดสินใจอีกไหม (อัปเกรดโฉนด / ไถ่ถอนที่จำนองไว้)
 * ใช้ตัดสินว่าจะรีบจบตาอัตโนมัติ หรือควรให้เวลาคิดก่อน
 * — การขายไม่นับ เพราะเป็นทางเลือกที่แทบไม่มีใครทำตอนไม่ได้ติดลบ
 */
export function hasTurnOptions(g: GameState): boolean {
  const p = cur(g)
  for (let id = 0; id < g.tiles.length; id++) {
    if (canUpgrade(g, p, id)) return true
    const ts = g.tiles[id]
    if (ts.ownerId === p.id && ts.mortgaged && canAfford(p, unmortgageCost(id))) return true
  }
  return false
}

export function endTurn(g: GameState): void {
  if (!canEndTurn(g)) return
  const p = cur(g)
  if (g.rollAgain && !p.bankrupt && p.quarantine === 0 && p.skipTurns === 0) {
    g.rollAgain = false
    g.dice = null
    g.phase = 'idle'
    pushLog(g, p.id, '🎲', `${p.name} ทอยได้แต้มคู่ — ได้ทอยอีกครั้ง!`, 'good')
    return
  }
  finishTurn(g)
}

function finishTurn(g: GameState): void {
  const p = cur(g)
  // นับเวลากักกันตอนจบตา ไม่ใช่ตอนเริ่ม — ไม่งั้นตาสุดท้ายจะถูกปล่อยให้ทอยได้เลย
  // ทำให้เสียตาจริงน้อยกว่าที่การ์ดบอกไปหนึ่งตา
  if (p.quarantine > 0) p.quarantine -= 1
  p.effects = tick(p.effects)
  checkFreedom(g, p)
  if (g.phase === 'gameover') return
  advanceTurn(g)
}

function advanceTurn(g: GameState): void {
  if (alivePlayers(g).length <= 1) {
    checkGameOver(g)
    return
  }
  let guard = 0
  do {
    g.current = (g.current + 1) % g.players.length
    if (g.current === 0) {
      g.round += 1
      g.globalEffects = tick(g.globalEffects)
    }
    guard += 1
  } while (g.players[g.current].bankrupt && guard < g.players.length * 4)
  beginTurn(g)
}

function beginTurn(g: GameState): void {
  const p = cur(g)
  g.dice = null
  g.rolling = false
  g.rollAgain = false
  p.doubles = 0
  p.rerollLeft = p.profession.perkId === 'lucky' ? 1 : 0

  if (p.skipTurns > 0) {
    p.skipTurns -= 1
    p.effects = tick(p.effects)
    pushLog(g, p.id, '🧊', `${p.name} ถูกแช่แข็ง — ข้ามตานี้`, 'bad')
    advanceTurn(g)
    return
  }

  if (p.quarantine > 0) {
    pushLog(g, p.id, '🧊', `${p.name} ติดอยู่ในสถานีกักกัน (เหลืออีก ${p.quarantine} ตา)`, 'neutral')
  }

  g.phase = 'idle'
}

/** เริ่มตาแรกของเกม (เรียกครั้งเดียวหลัง createGame) */
export function startGame(g: GameState): void {
  beginTurn(g)
}
